import { isAlgoliaIntegrationConfigured } from 'lib/algolia/config'
import { isClient } from 'shared/utils/common'


export const getCurrentAlgoliaInsightsUserToken = async (
  customerId?: string
): Promise<string | undefined> => {
  if (!isClient() || !window?.aa) return

  if (customerId) return customerId

  return new Promise<string | undefined>(r =>
    window.aa('getUserToken', null, (_: unknown, t?: string) => r(t))
  )
}

let anonymousUserToken: string | undefined

export const setAlgoliaInsightsUserToken = async (customerId?: string) => {
  if (!isClient() || !window?.aa) return

  // Read before the first override, so it still holds the library's own anonymous token.
  anonymousUserToken ??= await getCurrentAlgoliaInsightsUserToken()

  const userToken = customerId || anonymousUserToken

  // Nothing to restore - leave the library to generate its own anonymous token.
  if (!userToken) return

  window.aa('setUserToken', userToken)
}

interface AlgoliaInsightsClickEventParams {
  index: string
  queryID: string | null | undefined
  objectID: string
  position: number
  userToken?: string
}

// Middle-clicking (or ctrl/cmd-clicking) a result link opens it in a new tab via the
// browser's native `auxclick` event, which GTM's click-based trigger never observes -
// so the "Search Result Clicked" event configured there is silently skipped. Firing the
// Algolia Insights API call directly here covers that gap regardless of GTM's setup.
export const sendAlgoliaInsightsClickEvent = ({
  index,
  queryID,
  objectID,
  position,
  userToken
}: AlgoliaInsightsClickEventParams) => {
  if (!isClient() || !window?.aa || !queryID || !objectID) return

  window.aa('clickedObjectIDsAfterSearch', {
    eventName: 'Search Result Clicked',
    index,
    queryID,
    objectIDs: [objectID],
    positions: [position],
    userToken
  })
}

// `position` is 1-based, as Algolia expects. Product tiles resolve their
// objectID and position through getProductInsightsDataAttributes.
export const getAlgoliaInsightsDataAttributes = (
  queryID: string | null | undefined,
  objectID: string,
  position: number
) => {
  if (!isAlgoliaIntegrationConfigured || !queryID) return {}

  return {
    'data-insights-query-id': queryID,
    'data-insights-object-id': objectID,
    'data-insights-position': position
  }
}

declare global {
  interface Window {
    aa: (
      command: string,
      params: unknown,
      callback?: (_: unknown, result: string | undefined) => void
    ) => void
  }
}
